import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  DEFAULT_TRIAL_FILTERS,
  DEFAULT_TRIAL_SUMMARY,
  type TrialListFilters,
  type TrialTenant,
  type TrialsState,
} from '../types';
import {
  converterTrial,
  encerrarTrial,
  estenderTrial,
  fetchTrials,
  fetchTrialsByTenant,
  iniciarTrial,
} from './actions';

const initialState: TrialsState = {
  trials: [],
  summary: DEFAULT_TRIAL_SUMMARY,
  filters: DEFAULT_TRIAL_FILTERS,
  loading: false,
  saving: false,
  error: null,
};

const computeSummary = (trials: TrialTenant[]): TrialsState['summary'] => {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const startOfMonthMs = startOfMonth.getTime();

  return trials.reduce(
    (acc, trial) => {
      const expiracaoMs = new Date(trial.dataExpiracao).getTime();
      const atualizadoEmMs = new Date(trial.atualizadoEm).getTime();

      if (trial.status === 'ATIVO') {
        acc.totalAtivos += 1;
        if (expiracaoMs - now <= sevenDaysMs && expiracaoMs - now >= 0) {
          acc.vencendoEmAteSeteDias += 1;
        }
      }

      if (trial.status === 'EXPIRADO') {
        acc.expiradosSemAcao += 1;
      }

      if (trial.status === 'CONVERTIDO' && atualizadoEmMs >= startOfMonthMs) {
        acc.convertidosNoMes += 1;
      }

      return acc;
    },
    { ...DEFAULT_TRIAL_SUMMARY },
  );
};

const trialsSlice = createSlice({
  name: 'trials',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<TrialListFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = DEFAULT_TRIAL_FILTERS;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrials.fulfilled, (state, action: PayloadAction<TrialTenant[]>) => {
        state.loading = false;
        state.trials = action.payload;
        state.summary = computeSummary(action.payload);
      })
      .addCase(fetchTrials.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar trials';
      })

      .addCase(fetchTrialsByTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrialsByTenant.fulfilled, (state, action: PayloadAction<TrialTenant[]>) => {
        state.loading = false;
        const otherTrials = state.trials.filter(
          (trial) => !action.payload.some((p) => p.rootContractId === trial.rootContractId),
        );
        state.trials = [...otherTrials, ...action.payload];
        state.summary = computeSummary(state.trials);
      })
      .addCase(fetchTrialsByTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar histórico de trial';
      })

      .addCase(iniciarTrial.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(iniciarTrial.fulfilled, (state, action: PayloadAction<TrialTenant>) => {
        state.saving = false;
        state.trials = [action.payload, ...state.trials];
        state.summary = computeSummary(state.trials);
      })
      .addCase(iniciarTrial.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao iniciar trial';
      })

      .addCase(estenderTrial.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(estenderTrial.fulfilled, (state, action: PayloadAction<TrialTenant>) => {
        state.saving = false;
        state.trials = state.trials.map((t) =>
          t.rootContractId === action.payload.rootContractId ? action.payload : t,
        );
        state.summary = computeSummary(state.trials);
      })
      .addCase(estenderTrial.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao estender trial';
      })

      .addCase(converterTrial.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(converterTrial.fulfilled, (state, action: PayloadAction<TrialTenant>) => {
        state.saving = false;
        state.trials = state.trials.map((t) =>
          t.rootContractId === action.payload.rootContractId ? action.payload : t,
        );
        state.summary = computeSummary(state.trials);
      })
      .addCase(converterTrial.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao converter trial';
      })

      .addCase(encerrarTrial.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(encerrarTrial.fulfilled, (state, action: PayloadAction<TrialTenant>) => {
        state.saving = false;
        state.trials = state.trials.map((t) =>
          t.rootContractId === action.payload.rootContractId ? action.payload : t,
        );
        state.summary = computeSummary(state.trials);
      })
      .addCase(encerrarTrial.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao encerrar trial';
      });
  },
});

export const { setFilters, clearFilters, clearError } = trialsSlice.actions;
export const trialsReducer = trialsSlice.reducer;
